// The lane course (R3075, v2-the-deep.md §7.2 "The lane course",
// ui-map-interface.md §8 "The on-ramp offer"): the client twin of
// `server/internal/sim/lanecourse.go`.
//
// A lane course joins a track of a shipping lane of the faction at the
// point nearest the fleet, sails the track to the point nearest the
// target, and then sails to the target. An open track sails in the
// direction of travel; a closed track sails the faster of its two ways
// round. The time of a run counts each piece of half a hex as its length
// over the lift at its middle: lit water, or an armed lane light of the
// faction within its reach on a lane cell of the faction (`Sim.LiftAt`).
// A course counts when it saves `route_lane_use_margin` of the time of
// the direct run, and a run under `route_lane_use_min_px` goes direct.
//
// The map asks during a drag, so the walk runs on the client: a round trip
// for each drag frame is too slow. The SERVER judges the course the client
// sends as any course. The walk is a hand mirror, so
// `server/internal/hub/testdata/lane-course-vectors.json` holds both sides
// to one answer (`lanecourse.test.ts`). Keep the order of the server:
// tracks in the order given, the forward way before the back way, and the
// first of two equal times wins. The module is pure.

import type { ChainLeg, ChainTrack } from './lanechain';
import { loopParts } from './ui/plotline';
import { cellAt, cellCenterX, cellCenterY, COLS, HEX_SIZE, MPX_PER_PX, ROWS } from './world';

/** A point on the map, world px. */
export interface LanePt { x: number; y: number }

/** One armed lane light of the faction: its point, the square of its
 *  reach and its lift. */
export interface LaneLift { x: number; y: number; reach2: number; mul: number }

/** What the lift reads: the grid, the lit water of the fairways, the lane
 *  cells of the faction, the armed lane lights of the faction, the lift
 *  of lit water and the lift of a bare lane (R3217). */
export interface LaneWater {
  cols: number;
  rows: number;
  lit: Uint8Array;
  lane: Uint8Array;
  lights: readonly LaneLift[];
  buoyMul: number;
  laneMul: number;
}

/** One lane course: the join point, the points of the track between, the
 *  exit point and the target (each on the millipixel of the wire), its
 *  time, the time of the direct run, both in px of an unlifted hull, and
 *  the index of its track in the list given. */
export interface LaneRoute { legs: LanePt[]; time: number; direct: number; track: number }

/** A point on the millipixel of the wire: `world.Mpx` truncates. */
function mpx(x: number, y: number): LanePt {
  return { x: Math.trunc(x * MPX_PER_PX) / MPX_PER_PX, y: Math.trunc(y * MPX_PER_PX) / MPX_PER_PX };
}

function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx, dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * The lit water of the fairways (`World.MaskFairways`): the cells of each
 * chain from its first buoy to its last, along the chain. A buoy names its
 * chain counted over the chains of every fairway in order, and it stands
 * at the chain cell nearest it (the lowest index on a tie). A chain of
 * one buoy position, or none, lights nothing.
 */
export function litMask(chains: readonly (readonly number[])[], buoys: readonly { x: number; y: number; chain: number }[],
  cols = COLS, rows = ROWS): Uint8Array {
  const out = new Uint8Array(cols * rows);
  const first = chains.map((c) => c.length);
  const last = chains.map(() => -1);
  for (const b of buoys) {
    const chain = chains[b.chain];
    if (!chain) continue;
    let at = -1, best = 0;
    chain.forEach((c, i) => {
      const d = dist2(cellCenterX(c, cols), cellCenterY(c, cols), b.x, b.y);
      if (at < 0 || d < best) { at = i; best = d; }
    });
    first[b.chain] = Math.min(first[b.chain], at);
    last[b.chain] = Math.max(last[b.chain], at);
  }
  chains.forEach((chain, i) => {
    if (first[i] >= last[i]) return;
    for (let k = first[i]; k <= last[i]; k++) {
      const c = chain[k];
      if (c >= 0 && c < out.length) out[c] = 1;
    }
  });
  return out;
}

/**
 * Marks the lane cells of one lane in `out` (`laneCells` of the hub): the
 * cells its legs cross, read at each half hex along each leg, the leg back
 * of each loop part included; a lane of one leg marks its cell.
 */
export function markLaneCells(legs: readonly ChainLeg[], out: Uint8Array, cols = COLS, rows = ROWS): void {
  const leg = (a: LanePt, b: LanePt) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const n = Math.trunc(Math.hypot(dx, dy) / (HEX_SIZE / 2)) + 1;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const c = cellAt(a.x + dx * t, a.y + dy * t, cols, rows);
      if (c >= 0 && c < out.length) out[c] = 1;
    }
  };
  for (let i = 1; i < legs.length; i++) leg(legs[i - 1], legs[i]);
  if (legs.length === 1) leg(legs[0], legs[0]);
  for (const part of loopParts(legs)) {
    if (part.open < part.last) leg(legs[part.last], legs[part.open]);
  }
}

/** The lift a hull of the faction takes at (x, y): the lift of lit water,
 *  of a bare lane cell (R3217), or of an armed lane light within its reach
 *  on a lane cell; the strongest one, and 1 where nothing lifts. */
export function liftAt(w: LaneWater, x: number, y: number): number {
  let mul = 1;
  const cell = cellAt(x, y, w.cols, w.rows);
  if (cell >= 0 && w.lit[cell] === 1) mul = Math.max(mul, w.buoyMul);
  if (cell < 0 || w.lane[cell] !== 1) return mul;
  mul = Math.max(mul, w.laneMul);
  for (const l of w.lights) {
    if (dist2(l.x, l.y, x, y) <= l.reach2) mul = Math.max(mul, l.mul);
  }
  return mul;
}

/** The time of a run from `from` through the points, in px of an unlifted
 *  hull: each piece of half a hex counts its length over its lift. */
export function runTime(w: LaneWater, from: LanePt, pts: readonly LanePt[]): number {
  let t = 0;
  let a = from;
  for (const b of pts) {
    const dx = b.x - a.x, dy = b.y - a.y;
    const l = Math.hypot(dx, dy);
    const m = Math.trunc(l / (HEX_SIZE / 2)) + 1;
    const piece = l / m;
    for (let k = 0; k < m; k++) {
      const f = (k + 0.5) / m;
      t += piece / liftAt(w, a.x + dx * f, a.y + dy * f);
    }
    a = b;
  }
  return t;
}

/**
 * The runs of a course where the hull takes a lift (the lit stretches of
 * the course line, ui-map-interface.md §6.1): for each leg from `from`
 * through the points, the pieces of half a hex whose middle a lift
 * reaches, joined into runs, as x0, y0, x1, y1 in world px. The lift of a
 * bare lane does not count: a lit stretch is lit water or the reach of a
 * lane light (R3217).
 */
export function litRuns(w: LaneWater, from: LanePt, pts: readonly LanePt[]): number[] {
  const out: number[] = [];
  if (w.laneMul !== 1) w = { ...w, laneMul: 1 };
  let a = from;
  for (const b of pts) {
    const dx = b.x - a.x, dy = b.y - a.y;
    const m = Math.trunc(Math.hypot(dx, dy) / (HEX_SIZE / 2)) + 1;
    let start = -1;
    for (let k = 0; k <= m; k++) {
      const on = k < m && liftAt(w, a.x + dx * ((k + 0.5) / m), a.y + dy * ((k + 0.5) / m)) > 1;
      if (on && start < 0) start = k;
      if (!on && start >= 0) {
        out.push(a.x + dx * (start / m), a.y + dy * (start / m), a.x + dx * (k / m), a.y + dy * (k / m));
        start = -1;
      }
    }
    a = b;
  }
  return out;
}

function segs(t: ChainTrack): number { return t.closed ? t.pts.length : t.pts.length - 1; }

function trackLength(t: ChainTrack): number {
  let run = 0;
  for (let i = 0; i < segs(t); i++) {
    const a = t.pts[i], b = t.pts[(i + 1) % t.pts.length];
    run += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return run;
}

/** The point of a track nearest p: the index of its leg, the point, and
 *  its distance along the track. A tie keeps the first leg. */
function trackNearest(t: ChainTrack, p: LanePt): [number, LanePt, number] {
  let bestI = 0, bestD = Infinity, bestS = 0, along = 0;
  let bestP: LanePt = { x: 0, y: 0 };
  for (let i = 0; i < segs(t); i++) {
    const a = t.pts[i], b = t.pts[(i + 1) % t.pts.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    let u = 0;
    if (l2 > 0) u = Math.min(Math.max(((p.x - a[0]) * dx + (p.y - a[1]) * dy) / l2, 0), 1);
    const px = a[0] + dx * u, py = a[1] + dy * u;
    const l = Math.hypot(dx, dy);
    const d = dist2(p.x, p.y, px, py);
    if (d < bestD) { bestI = i; bestD = d; bestP = { x: px, y: py }; bestS = along + l * u; }
    along += l;
  }
  return [bestI, bestP, bestS];
}

/** One way along a track: the join point, k points of the track from
 *  index `first` in steps of `dir`, the exit point and the target; a point
 *  within 1 px of the one before it (the fleet first) drops. */
function wayAlong(t: ChainTrack, from: LanePt, to: LanePt, a: LanePt, b: LanePt, first: number, k: number, dir: number): LanePt[] {
  const m = t.pts.length;
  const out: LanePt[] = [];
  let last = from;
  const add = (p: LanePt) => {
    const q = mpx(p.x, p.y);
    if (dist2(q.x, q.y, last.x, last.y) >= 1) { out.push(q); last = q; }
  };
  add(a);
  for (let j = 0; j < k; j++) {
    const p = t.pts[(((first + dir * j) % m) + m) % m];
    add({ x: p[0], y: p[1] });
  }
  add(b);
  add(to);
  return out;
}

/** The lane courses along one track: one for an open track, forward and
 *  back for a closed one; null for a way that carries the fleet less than
 *  one hex size along the track. */
function trackWays(t: ChainTrack, from: LanePt, to: LanePt): (LanePt[] | null)[] {
  const m = t.pts.length;
  const [ia, a, sa] = trackNearest(t, from);
  const [ib, b, sb] = trackNearest(t, to);
  if (!t.closed) {
    if (Math.abs(sb - sa) < HEX_SIZE) return [];
    return sa < sb ? [wayAlong(t, from, to, a, b, ia + 1, ib - ia, 1)] : [wayAlong(t, from, to, a, b, ia, ia - ib, -1)];
  }
  const total = trackLength(t);
  const fwd = (sb - sa + total) % total, back = (sa - sb + total) % total;
  const out: (LanePt[] | null)[] = [null, null];
  if (fwd >= HEX_SIZE) {
    let k = (ib - ia + m) % m;
    if (k === 0 && sb < sa) k = m;
    out[0] = wayAlong(t, from, to, a, b, ia + 1, k, 1);
  }
  if (back >= HEX_SIZE) {
    let k = (ia - ib + m) % m;
    if (k === 0 && sb > sa) k = m;
    out[1] = wayAlong(t, from, to, a, b, ia, k, -1);
  }
  return out;
}

/**
 * The lane course of the least time from `from` to `to` over the tracks
 * (the shipping lanes of the faction), when it takes less than 1 − margin
 * of the time of the direct run and holds at most `legsMax` legs; null
 * otherwise, and for a run shorter than `minPx`. The cost is bounded by
 * the lanes: one nearest walk of each track and one time of each way.
 */
export function bestLaneCourse(tracks: readonly ChainTrack[], from: LanePt, to: LanePt, w: LaneWater,
  margin: number, minPx: number, legsMax: number): LaneRoute | null {
  if (dist2(from.x, from.y, to.x, to.y) < minPx * minPx) return null;
  const end = mpx(to.x, to.y);
  const direct = runTime(w, from, [end]);
  let best: LaneRoute | null = null;
  let limit = direct * (1 - margin);
  tracks.forEach((t, i) => {
    if (t.pts.length < 2) return;
    for (const way of trackWays(t, from, end)) {
      if (!way || way.length === 0 || way.length > legsMax) continue;
      const rt = runTime(w, from, way);
      if (rt < limit) { best = { legs: way, time: rt, direct, track: i }; limit = rt; }
    }
  });
  return best;
}
